import {
  DestroyRef,
  Directive,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { map, merge, startWith, Subscription, take, throttleTime } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Analytics } from '@recruitee/analytics';
import { filterBoolean } from '@recruitee/common';
import {
  ComponentPositionPair,
  ComponentTooltipPosition,
  ComponentTooltipService,
  DialogOpenerService,
  ResizeObserverEntryDirective,
  OVERLAY_LEVEL,
} from '@recruitee/user-interface';

import { FeatureTooltipData, FeatureTooltipDirectiveConfig } from '../../product-updates.types';
import { GlobalProductUpdatesState } from '../../services/global-product-updates.state';
import { FeatureTooltipWrapperComponent } from './feature-tooltip-wrapper.component';

@Directive({
  selector: '[rtFeatureTooltip]',
  providers: [ComponentTooltipService],
  hostDirectives: [ResizeObserverEntryDirective],
  standalone: true,
})
export class FeatureTooltipWrapperDirective implements OnChanges, OnInit {
  @Input() public rtFeatureTooltip: FeatureTooltipDirectiveConfig;
  @Input('rtFeatureTooltipEnabled') public isEnabled: boolean;

  @Output() public rtFeatureTooltipDismissed: EventEmitter<void> = new EventEmitter<void>();
  @Output() public rtFeatureTooltipLearnMoreClick: EventEmitter<void> = new EventEmitter<void>();

  private destroyRef = inject(DestroyRef);
  private resizeDirective = inject(ResizeObserverEntryDirective);
  private overlayLevel = inject(OVERLAY_LEVEL);
  private openSubscription: Subscription | null = null;
  private readonly positions = {
    [ComponentTooltipPosition.BottomLeft]: new ComponentPositionPair(
      { originX: 'start', originY: 'bottom' },
      { overlayX: 'start', overlayY: 'top' },
      'lefttop',
    ),
    [ComponentTooltipPosition.BottomRight]: new ComponentPositionPair(
      { originX: 'end', originY: 'bottom' },
      { overlayX: 'end', overlayY: 'top' },
      'righttop',
    ),
    [ComponentTooltipPosition.TopLeft]: new ComponentPositionPair(
      { originX: 'start', originY: 'top' },
      { overlayX: 'start', overlayY: 'bottom' },
      'leftbottom',
    ),
    [ComponentTooltipPosition.TopRight]: new ComponentPositionPair(
      { originX: 'end', originY: 'top' },
      { overlayX: 'end', overlayY: 'bottom' },
      'rightbottom',
    ),
    [ComponentTooltipPosition.Left]: new ComponentPositionPair(
      { originX: 'start', originY: 'center' },
      { overlayX: 'end', overlayY: 'center' },
      'right',
    ),
    [ComponentTooltipPosition.Right]: new ComponentPositionPair(
      { originX: 'end', originY: 'center' },
      { overlayX: 'start', overlayY: 'center' },
      'left',
    ),
  };

  private globalProductUpdatesState = inject(GlobalProductUpdatesState);

  constructor(
    private componentTooltipService: ComponentTooltipService<
      typeof FeatureTooltipWrapperComponent,
      FeatureTooltipData
    >,
    private analytics: Analytics,
    private dialogService: DialogOpenerService,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.rtFeatureTooltip) {
      this.componentTooltipService.setComponentData(this.rtFeatureTooltip);
    }

    if (changes.isEnabled && this.componentTooltipService.hasConfig()) {
      this.isEnabled ? this.open() : this.close();
    }
  }

  public ngOnInit(): void {
    this.componentTooltipService.setConfig({
      context: this.rtFeatureTooltip.context,
      positions: this.rtFeatureTooltip.positions || this.positions,
      overlayClipArea: this.rtFeatureTooltip.overlayClipArea,
      hasBackdrop: false,
      ...(this.rtFeatureTooltip.observeOnElement
        ? { observeOnElement: this.rtFeatureTooltip.observeOnElement }
        : {}),
      ...(this.rtFeatureTooltip.observeOnCustomElement
        ? { observeOnCustomElement: this.rtFeatureTooltip.observeOnCustomElement }
        : {}),
      withFallbackPoint: true,
    });
    this.componentTooltipService.setComponentData(this.rtFeatureTooltip);
    this.componentTooltipService.setComponent(FeatureTooltipWrapperComponent);

    this.resizeDirective.resized
      .pipe(
        throttleTime(16, undefined, { leading: true, trailing: true }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (!this.componentTooltipService.isOpen()) return;

        this.componentTooltipService.updateTooltipPosition();
      });

    if (this.isEnabled) {
      this.open();
    }
  }

  public close(): void {
    this.openSubscription?.unsubscribe();
    this.openSubscription = null;
    this.componentTooltipService.closeTooltip();
  }

  private open() {
    if (!this.globalProductUpdatesState.enabled) return;

    this.openSubscription?.unsubscribe();
    this.openSubscription = merge(
      ...this.dialogService.openDialogs.map(dialogRef => dialogRef.afterClosed),
    )
      .pipe(
        startWith(null),
        map(() => this.canOpenTooltip()),
        filterBoolean,
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.openTooltip();
        this.openSubscription = null;
      });
  }

  private canOpenTooltip() {
    const maxLevel = Math.max(
      ...this.dialogService.openDialogs.map(dialogRef => dialogRef.containerInstance.overlayLevel),
    );
    return maxLevel <= this.overlayLevel;
  }

  private openTooltip(): void {
    const { instance, changeDetectorRef } = this.componentTooltipService.openTooltip();
    changeDetectorRef.detectChanges();

    instance.close$.pipe(takeUntil(instance.ngOnDestroy$)).subscribe(() => {
      this.rtFeatureTooltipDismissed.emit();
      this.close();
    });

    instance.learnMoreClick$.pipe(takeUntil(instance.ngOnDestroy$)).subscribe(() => {
      this.analytics.track(`[Feature tooltip] Learn more ${this.rtFeatureTooltip.title}`);
      this.rtFeatureTooltipLearnMoreClick.emit();
    });
  }
}
